<script lang="ts">
	import { format } from 'svelte-i18n';
	import { user_data } from 'src/stores/account/user';
	import type { Device } from 'src/types/Device';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { VideoContainerState } from 'src/components/Camera/types/video-container';
	import {
		Icon,
		LabelListV2,
		Text,
		type IconPropsTypes,
		type LabelListV2PropsTypes,
		type LabelProps,
		type TextPropsTypes,
	} from '@Shelly/shelly-design-system';
	import {
		getCameraComponentKey,
		isCameraPrivacyEnabled,
	} from 'src/components/Camera/helpers/camera-helpers';
	import {
		computeCameraState,
		isCameraFirmwareUpdateRequired,
		isCameraPlaceholderState,
	} from 'src/components/Camera/helpers/camera-state-helpers';
	import { hasActiveWebRTCSession } from 'src/components/Camera/helpers/webrtc-session-manager';
	import { getCustomizeLabelList } from 'src/components/DevicesSettings/CustomizeDeviceWizard/customize-device-wizard-helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';

	export let device: Device | undefined;
	export let status: DeviceStatusG2 | undefined;
	export let settings: DeviceSettingsG2 | undefined;
	export let compact = false;
	/** Smaller status chips for dashboard camera button cards. */
	export let denseOverlay = false;
	/** Light label styling when rendered over a camera preview. */
	export let onDark = false;
	/** Align labels to the end (right in LTR). Default start (top-left) for dashboard camera headers. */
	export let alignEnd = false;
	/** Icon-only status chips when the tile is too narrow for labels. */
	export let iconOnly = false;
	/** Measured tile size. Motion/status copy scales with this so large tiles stay readable. */
	export let overlaySize: 's' | 'm' | 'l' = 's';
	/** Merged player + camera state from a live tile; when set, overlay follows it. */
	export let overlayState: VideoContainerState | undefined;

	type StatusLabel = LabelProps & { name?: string };

	/** Offline means the device is unreachable through the cloud, not a camera fault. */
	const offlineChipIcon = 'fa-light fa-cloud-slash';

	const getLabelName = (label: LabelProps): string | undefined => (label as StatusLabel).name;
	const isMotionLabel = (label: LabelProps) => getLabelName(label) === 'motion';
	const isRecordingLabel = (label: LabelProps) => getLabelName(label) === 'recording';
	const isLiveHudLabel = (label: LabelProps) => isMotionLabel(label) || isRecordingLabel(label);

	const getLabelSize = (
		iconOnly: boolean,
		overlay: typeof overlaySize,
	): LabelListV2PropsTypes['size'] => {
		if (iconOnly) return 'xs';
		if (overlay === 'l') return 'm';
		if (overlay === 'm') return 's';
		return 'xs';
	};
	const getConnectionIconSize = (overlay: typeof overlaySize): IconPropsTypes['size'] =>
		overlay === 'l' ? 's' : 'xs';
	const getChipTextStyle = (overlay: typeof overlaySize): NonNullable<TextPropsTypes['styleType']> => {
		if (overlay === 'l') return 'body-extra-small';
		if (overlay === 'm') return 'tiny';
		return 'miniature';
	};

	$: cameraKey = device ? getCameraComponentKey(device) : '';
	$: isOnline = !!status?.online;
	$: privacyMode = isCameraPrivacyEnabled(device, status, settings);
	$: isSupportAccess = $user_data?.temporary === true;
	$: streamer = (status?.[cameraKey] as { streamer?: string } | undefined)?.streamer;
	$: firmwareUpdateRequired = isCameraFirmwareUpdateRequired(device, status);
	$: streams = (status?.[cameraKey] as { streams?: number } | undefined)?.streams;
	$: computedCameraState =
		device && status
			? computeCameraState(
					privacyMode,
					isOnline,
					streamer,
					firmwareUpdateRequired,
					streams,
					hasActiveWebRTCSession(device.id),
					isSupportAccess,
				)
			: null;
	$: cameraState = overlayState !== undefined ? overlayState : computedCameraState;
	$: statuses = device && status && settings ? status_configurator(status, device, settings) : [];
	$: baseLabels = device && statuses?.length ? getCustomizeLabelList(device, statuses) : [];
	$: iconOnlyStatus = iconOnly;

	$: streamLabels = (() => {
		const labels: typeof baseLabels = [];
		if (!device || !status || !isOnline) return labels;
		if (!cameraState || cameraState.type === 'loading' || cameraState.type === 'offline') {
			return labels;
		}

		if (cameraState.type === 'support_access') {
			labels.push({
				icon: cameraState.icon || 'fas fa-video-slash',
				value: iconOnlyStatus
					? ''
					: cameraState.heading ||
						$format('camera_support_access', { default: 'Support Access' }),
				iconColor: '--color-warning',
			});
			return labels;
		}

		if (cameraState.type === 'privacy') {
			labels.push({
				icon: cameraState.icon || 'fas fa-video-slash',
				value: iconOnlyStatus
					? ''
					: cameraState.heading || $format('camera_privacy_mode', { default: 'Privacy Mode' }),
				iconColor: '--color-warning',
			});
			return labels;
		}

		if (cameraState.type === 'firmware') {
			labels.push({
				icon: cameraState.icon || 'fal fa-cloud-arrow-up',
				value: iconOnlyStatus
					? ''
					: cameraState.heading ||
						$format('alert_heading_update_required', {
							default: 'Firmware update required',
						}),
				iconColor: '--color-warning',
			});
			return labels;
		}

		if (cameraState.type === 'error') {
			labels.push({
				icon: cameraState.icon || 'fal fa-camera-slash',
				value: iconOnlyStatus
					? ''
					: cameraState.heading || $format('camera_stream_error', { default: 'Stream error' }),
				iconColor: '--color-danger',
			});
			return labels;
		}

		labels.push({
			icon: cameraState.icon || 'fal fa-spinner-third fa-spin',
			value: iconOnlyStatus
				? ''
				: cameraState.heading || $format('camera_starting', { default: 'Starting camera' }),
			iconColor: '--color-muted',
		});

		return labels;
	})();

	$: showOfflineChip = !!device && !!status && !isOnline;

	$: hideLiveHud = !isOnline || isCameraPlaceholderState(cameraState);
	$: filteredMotionLabels = hideLiveHud
		? baseLabels.filter((label) => !isLiveHudLabel(label))
		: iconOnlyStatus
			? baseLabels
					.filter(
						(label) =>
							!isMotionLabel(label) ||
							(typeof label.icon === 'string' && label.icon.includes('person-walking')),
					)
					.map((label) => (isLiveHudLabel(label) ? { ...label, value: '' } : label))
			: baseLabels;
	$: labelList = isOnline ? [...filteredMotionLabels, ...streamLabels] : filteredMotionLabels;
	$: labelSize = getLabelSize(iconOnlyStatus, overlaySize);
	$: connectionIconSize = getConnectionIconSize(overlaySize);
	$: chipTextStyle = getChipTextStyle(overlaySize);
	$: hasVisibleLabels = showOfflineChip || labelList.length > 0;
	$: connectionChipText =
		showOfflineChip && !iconOnlyStatus
			? $format('device_status_no_connection', { default: 'Device is offline' })
			: '';
</script>

{#if hasVisibleLabels}
	<div
		class="dash-camera-card-info-line"
		class:compact
		class:dense-overlay={denseOverlay}
		class:on-dark={onDark}
		class:align-end={alignEnd}
		class:size-s={overlaySize === 's'}
		class:size-m={overlaySize === 'm'}
		class:size-l={overlaySize === 'l'}
	>
		<div class="camera-status-labels-row" class:align-end={alignEnd}>
			{#if labelList.length}
				<LabelListV2
					{labelList}
					size={labelSize}
					a11yProps={{ isInteractive: false }}
					isDefaultCursor
				/>
			{/if}
			{#if showOfflineChip}
				<div
					class="camera-status-chip"
					class:camera-cloud-icon={iconOnlyStatus}
					role="status"
					aria-label={$format('device_status_no_connection', {
						default: 'Device is offline',
					})}
				>
					<Icon
						icon={offlineChipIcon}
						size={connectionIconSize}
						class={onDark ? 'text-white' : 'text-color-muted'}
					/>
					{#if connectionChipText}
						<Text
							styleType={chipTextStyle}
							class="camera-status-chip-text {onDark ? 'text-white' : ''}"
							noWrap
						>
							{connectionChipText}
						</Text>
					{/if}
				</div>
			{/if}
		</div>
	</div>
{/if}

<style>
	.dash-camera-card-info-line {
		min-width: 0;
		max-width: 100%;
	}

	.camera-status-labels-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--spacing-xs);
	}

	.camera-status-labels-row.align-end {
		justify-content: flex-end;
	}

	.dash-camera-card-info-line.align-end {
		margin-left: auto;
	}

	.camera-status-chip {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-xs);
		min-height: 24px;
		padding: var(--spacing-xs) var(--spacing-s);
		border-radius: var(--radius-small);
		background-color: transparent;
		flex-shrink: 0;
	}

	.camera-status-chip.camera-cloud-icon {
		padding: var(--spacing-xxs) var(--spacing-xs);
		min-height: 0;
	}

	.dash-camera-card-info-line.size-s.compact .camera-status-chip,
	.dash-camera-card-info-line.size-s.dense-overlay .camera-status-chip {
		min-height: 20px;
		padding: var(--spacing-xxs) var(--spacing-xs);
		gap: var(--spacing-xxs);
	}

	.dash-camera-card-info-line :global(.label-list-v2) {
		flex-wrap: wrap;
		justify-content: flex-start;
	}

	.dash-camera-card-info-line.align-end .camera-status-labels-row {
		justify-content: flex-end;
	}

	.dash-camera-card-info-line.align-end :global(.label-list-v2) {
		justify-content: flex-end;
	}

	.dash-camera-card-info-line.size-s.dense-overlay :global(.label-list-v2) {
		gap: var(--spacing-xxs);
	}

	.dash-camera-card-info-line.size-s.dense-overlay :global(.label-v2) {
		min-height: 20px;
		padding: var(--spacing-xxs) var(--spacing-xs);
	}

	.dash-camera-card-info-line.size-s.dense-overlay :global(.label-v2 .icon-grid) {
		font-size: 11px;
		width: 11px;
		height: 11px;
	}

	.dash-camera-card-info-line.on-dark :global(.label-v2) {
		color: var(--color-white);
	}

	.dash-camera-card-info-line.on-dark :global(.label-v2 .icon-grid) {
		color: var(--color-white);
	}
</style>
